import Tooltip from '@mui/material/Tooltip';
import { FormikContext, useFormik, getIn, ErrorMessage, Field } from 'formik';
import React, { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { Input } from 'reactstrap';
import { SelectOneTask as SelectOneTaskType, TaskKeysType, WorkType } from 'shared/types';
import { TaskName } from 'shared/ui';
import LineForm from './components/LineForm';
import { audioApi, pdfApi, wordApi, taskApi, videoApi } from '../../../api';
import { imgApi } from '../../../api/img-api';
import trashSvg from '../../../assets/img/trash.svg';
import { selectOneTaskSchema, SelectOneTaskLine } from '../../../shared/types/task';
import Clue from '../../../shared/ui/Clue';
import TaskWrapper from '../TaskWrapper';
import InputPoint from 'Components/InputPoint/InputPoint';

export type SelectOneTaskProps = SelectOneTaskType & {
  handleDelete: (id: string) => void;
  userType: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  currentICLId: string;
  updateICL: boolean;
  workType?: WorkType;
  index: number;
  points?: number;
};

function SelectOneTask({
  id,
  handleDelete,
  completedAt,
  text,
  subText,
  lines,
  handleChangeTaskByKey,
  hasAnswer,
  hasStar,
  indexNumber,
  isActive,
  createdAt,
  type,
  currentICLId,
  updateICL,
  hasPhoto,
  deadline,
  audioUrl,
  videoUrl,
  pdfUrl,
  wordUrl,
  taskPhotos,
  answersPhotos,
  editedAnswerPhotos,
  workType,
  studentPdfUrl,
  studentWordUrl,
  index,
}: SelectOneTaskProps) {
  const { t } = useTranslation();
  const [taskId, setTaskId] = useState<string>('');
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);
  const isExam = workType === WorkType.exam;

  useEffect(() => {
    const init = async () => {
      try {
        const task = await taskApi.getTask(currentICLId, id);

        if (task) {
          setTaskId(task.id);
        }
      } catch (error) {
        console.log(error);
      }
    };

    init(); // to check if task exists in Firestore
  }, []);

  const initialFormValues = {
    text: updateICL ? text.replace(/<br>/g, '\n') : '',
    id,
    indexNumber,
    isActive,
    createdAt,
    type,
    hasAnswer,
    hasStar,
    hasPhoto,
    lines,
  };

  const formValues = isExam ? { ...initialFormValues, points: 0 } : initialFormValues;

  const formik = useFormik({
    initialValues: formValues,
    onSubmit: async values => {
      const textWithLB = values.text.replace(/\n/g, '<br>');

      if (!taskId) {
        await taskApi
          .addTask<SelectOneTaskType>(currentICLId, { ...values, indexNumber, text: textWithLB, completedAt })
          .then(id => {
            setTaskId(id);
            toast.success(t('Successfully added!'));

            return id;
          });
      } else {
        await taskApi.updateTask<SelectOneTaskType>(currentICLId, {
          ...values,
          text: textWithLB,
          completedAt,
        });
        toast.success(t('Successfully edited!'));
      }
    },
    validationSchema: selectOneTaskSchema,
  });

  const deleteTask = async () => {
    if (!taskId) {
      handleDelete(id);
    }

    if (updateICL || taskId) {
      handleDelete(id);

      setIsRemovingLoading(true);
      wordUrl && (await wordApi.removeWord(wordUrl, currentICLId, id));
      pdfUrl && (await pdfApi.removePdf(pdfUrl, currentICLId, id));
      studentWordUrl && (await wordApi.removeWord(studentWordUrl, currentICLId, id, true));
      studentPdfUrl && (await pdfApi.removePdf(studentPdfUrl, currentICLId, id, true));
      videoUrl && (await videoApi.removeVideo(videoUrl, currentICLId, id));
      audioUrl && (await audioApi.removeAudio(audioUrl, currentICLId, id));
      taskPhotos &&
        taskPhotos.forEach(async photo => await imgApi.removeImg(photo, currentICLId, id, taskPhotos, 'taskPhotos'));
      answersPhotos &&
        answersPhotos.forEach(
          async photo => await imgApi.removeImg(photo, currentICLId, id, answersPhotos, 'answersPhotos'),
        );
      editedAnswerPhotos &&
        editedAnswerPhotos.forEach(
          async photo => await imgApi.removeImg(photo, currentICLId, id, editedAnswerPhotos, 'editedAnswerPhotos'),
        );
      await taskApi.removeTask<SelectOneTaskType>(currentICLId, id);
      setIsRemovingLoading(false);
    }
  };

  const handleChangeTaskByKeyWithFormik = (id: string, key: TaskKeysType, value: string | boolean | string[]) => {
    handleChangeTaskByKey(id, key, value);

    formik.setFieldValue(key, value);
  };

  const handleAddLine = (line: SelectOneTaskLine) => {
    const newLines = formik.values.lines;
    newLines.push(line);

    formik.setFieldValue('lines', newLines);
  };

  const handleRemoveLine = (lineUuid: string) => {
    formik.setFieldValue(
      'lines',
      formik.values.lines.filter(it => it.id !== lineUuid),
    );
  };

  const getWordCheckedStatus = (lineUuid: string, wordId: number, word: 'first' | 'second'): boolean | null => {
    const lineId = formik.values.lines.findIndex(l => l.id === lineUuid);

    const path = `lines[${lineId}].words[${wordId}].firstCorrect`;
    let value = getIn(formik.values, path) as boolean | null;
    if (word === 'second' && typeof value === 'boolean') value = !value;

    return value;
  };

  const setWordCheckedStatus = (lineUuid: string, wordId: number, word: 'first' | 'second', value: boolean | null) => {
    const lineId = formik.values.lines.findIndex(l => l.id === lineUuid);

    const path = `lines[${lineId}].words[${wordId}].firstCorrect`;
    if (word === 'second' && typeof value === 'boolean') value = !value;

    formik.setFieldValue(path, value);
  };

  const handleWordClick = (lineUuid: string, wordId: number, word: 'first' | 'second') => {
    let value = getWordCheckedStatus(lineUuid, wordId, word);

    if (value === null) value = true;
    else if (value === false) value = true;
    else value = null;

    setWordCheckedStatus(lineUuid, wordId, word, value);
  };

  const renderLines = () => {
    return formik.values.lines.map(({ id: lineId, words, description, descriptionAfter }, lineIndex) => (
      <div key={lineId} className="flex w-full items-center justify-between">
        <div className="flex items-center">
          <div className="m-[8px] self-start text-[18px] font-medium text-[#242C2C]">
            {lineIndex + 1}. {description}
          </div>

          <div className="!ml-2	flex flex-wrap items-center gap-y-[12px] text-[18px] font-medium text-[#242C2C]">
            {words.map((word, wordId) => {
              if (typeof word === 'object') {
                const { first, second } = word;
                const firstValue = getWordCheckedStatus(lineId, wordId, 'first');
                const secondValue = getWordCheckedStatus(lineId, wordId, 'second');
                const wordError = getIn(formik.errors, `lines[${lineIndex}].words[${wordId}].firstCorrect`);

                return (
                  <div key={wordId}>
                    <div className="mr-[8px] flex flex-wrap items-center gap-[12px]">
                      <div className="flex items-center gap-[10px] rounded-[10px] bg-[#DCF0FF] p-[10px]">
                        <Input
                          type="checkbox"
                          className="mt-0 h-[20px] w-[20px] cursor-pointer border-2 border-[#4383F2] checked:border-[#4383F2] checked:bg-[#4383F2] checked:ring-[#4383F2] checked:ring-opacity-50 focus:border-[#4383F2] focus:ring-[#4383F2] focus:ring-opacity-50"
                          onChange={() => handleWordClick(lineId, wordId, 'first')}
                          checked={firstValue ?? undefined}
                        />

                        {first}
                      </div>

                      <div>/</div>

                      <div className="flex items-center gap-[10px] rounded-[10px] bg-[#DCF0FF] p-[10px]">
                        <Input
                          type="checkbox"
                          className="mt-0 h-[20px] w-[20px] cursor-pointer border-2 border-[#4383F2] checked:border-[#4383F2] checked:bg-[#4383F2] checked:ring-[#4383F2] checked:ring-opacity-50 focus:border-[#4383F2] focus:ring-[#4383F2] focus:ring-opacity-50"
                          onChange={() => handleWordClick(lineId, wordId, 'second')}
                          checked={secondValue ?? undefined}
                        />

                        {second}
                      </div>
                    </div>

                    {/* It seems correct but it doesn't work */}
                    {/* <ErrorMessage
                      name={`lines[${lineIndex}].words[${wordId}].firstCorrect`}
                      render={(msg: string) => <p className="h-fit text-sm font-medium text-red-600">{t(msg)}</p>}
                    /> */}
                    {wordError && <p className="h-fit text-sm font-medium text-red-600">{t(wordError)}</p>}
                  </div>
                );
              } else
                return (
                  <div className="mr-[8px]" key={wordId}>
                    {word}
                  </div>
                );
            })}
          </div>

          <div className="m-[8px] self-start text-[18px] font-medium text-[#242C2C]">{descriptionAfter}</div>
        </div>

        <Tooltip
          title={t('delete')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[90px] shrink-0 rounded-[8px] !p-[6px] text-red-600 lg:w-[40px]"
            onClick={() => handleRemoveLine(lineId)}
          >
            <img src={trashSvg} alt="mic" />
          </button>
        </Tooltip>
      </div>
    ));
  };

  return (
    <TaskWrapper
      id={id}
      handleDelete={deleteTask}
      completedAt={completedAt}
      handleChangeTaskByKey={handleChangeTaskByKeyWithFormik}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      save={formik.submitForm}
      formikProp={formik}
      updateICL={updateICL}
      isDeleteButtonDisabled={isRemovingLoading}
      hasPhoto={hasPhoto}
      deadline={deadline}
      isActive={isActive}
      currentICLId={currentICLId}
      audioUrl={audioUrl}
      remoteTaskId={taskId}
      videoUrl={videoUrl}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      taskPhotos={taskPhotos}
      workType={workType}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      index={index}
    >
      <FormikContext.Provider value={formik}>
        <div className="!mb-4 lg:!mb-5">
          <TaskName />
        </div>

        {isExam && (
          <InputPoint value={formik.values.points || 0} onChange={value => formik.setFieldValue('points', value)} />
        )}

        <div className="relative !mb-4 lg:!mb-5">
          <div className="text-[14px] font-bold uppercase lg:text-[18px]">{t('exercise heading')}:</div>

          <Field
            as="textarea"
            name="text"
            className={
              formik.errors.text
                ? 'h-[83px] w-full rounded-[20px] border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#FA6238_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
                : 'h-[83px] w-full rounded-[20px] border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
            }
            placeholder={t('enter task title')}
          />

          <ErrorMessage
            name="text"
            render={(msg: string) => (
              <p className="absolute left-0 top-14 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:top-[60px]">
                {t(msg)}
              </p>
            )}
          />
        </div>

        <div className="relative !mb-4 lg:!mb-5">
          <Clue text={'selectOneTaskClue'} />

          <ErrorMessage
            name="lines"
            render={(msg: string) =>
              typeof msg === 'string' && (
                <p className="top-13 absolute left-0 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:top-[80px]">
                  {t(msg)}
                </p>
              )
            }
          />

          <div className="mt-[8px] flex flex-col gap-4">{renderLines()}</div>

          <LineForm handleAdd={handleAddLine} />
        </div>
      </FormikContext.Provider>
    </TaskWrapper>
  );
}

export default memo(SelectOneTask);
